import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Download, Calendar, Eye, Users, Truck, Wrench, Package, Euro } from "lucide-react";
import axios from "axios";
import jsPDF from "jspdf";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const REPORT_TYPES = [
  { id: "sold", label: "Scutere vandute", icon: Euro, color: "text-emerald-400" },
  { id: "rar", label: "Programate RAR", icon: FileText, color: "text-blue-400" },
  { id: "delivered", label: "Transportate la client", icon: Truck, color: "text-orange-400" },
  { id: "registered", label: "Scutere inregistrate", icon: Package, color: "text-primary" },
  { id: "repair", label: "Programate reparatie", icon: Wrench, color: "text-yellow-400" },
  { id: "auction_clients", label: "Clienti licitatie (nume, tel, email)", icon: Users, color: "text-purple-400" },
  { id: "buyer_clients", label: "Clienti cumparatori (+ scuter, pret)", icon: Users, color: "text-pink-400" },
  { id: "custom_selection", label: "Selectie personalizata scutere", icon: FileText, color: "text-cyan-400" },
  { id: "views", label: "Raport vizualizari", icon: Eye, color: "text-red-400" },
];

function generateReportPDF(data, title, columns, dateFrom, dateTo) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const w = 297, margin = 8;

  doc.setFillColor(18, 18, 18);
  doc.rect(0, 0, w, 22, "F");
  doc.setTextColor(80, 220, 100);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(`SCUTERSH - ${title}`, margin, 10);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.text(`Perioada: ${dateFrom || "tot"} - ${dateTo || "tot"} | ${data.length} inregistrari | Generat: ${new Date().toLocaleDateString("ro-RO")}`, margin, 18);

  let y = 28;
  const colW = (w - margin * 2) / columns.length;

  doc.setFillColor(50, 50, 50);
  doc.rect(margin, y, w - margin * 2, 6, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(6);
  doc.setFont("helvetica", "bold");
  columns.forEach((col, i) => doc.text(col.label, margin + i * colW + 1, y + 4));
  y += 6;

  doc.setFont("helvetica", "normal");
  doc.setTextColor(30, 30, 30);
  data.forEach((row, idx) => {
    if (y > 195) { doc.addPage(); y = 12; }
    doc.setFillColor(idx % 2 === 0 ? 245 : 255, idx % 2 === 0 ? 245 : 255, idx % 2 === 0 ? 245 : 255);
    doc.rect(margin, y, w - margin * 2, 5.5, "F");
    doc.setFontSize(5.5);
    columns.forEach((col, i) => {
      const val = String(col.key === "_idx" ? idx + 1 : row[col.key] || "");
      doc.text(val.substring(0, Math.floor(colW / 2)), margin + i * colW + 1, y + 4);
    });
    y += 5.5;
  });

  y += 5;
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, y, w - margin, y);
  doc.setTextColor(120, 120, 120);
  doc.setFontSize(5);
  doc.text("SCUTERSH - Sura Mare, Sector 4, Bucuresti | +40792250058", w / 2, y + 4, { align: "center" });
  return doc;
}

function generateCSV(data, columns) {
  const header = columns.map(c => c.label).join(",");
  const rows = data.map((row, idx) => columns.map(c => {
    const val = c.key === "_idx" ? idx + 1 : row[c.key] || "";
    return `"${String(val).replace(/"/g, '""')}"`;
  }).join(","));
  return header + "\n" + rows.join("\n");
}

export default function ReportsPage() {
  const [reportType, setReportType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewsData, setViewsData] = useState(null);

  const fetchReport = async () => {
    if (!reportType) { toast.error("Selectati tipul de raport"); return; }
    setLoading(true);
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (reportType === "auction_clients") {
        const res = await axios.get(`${API}/admin/reports/auction-clients`, { headers });
        setData(res.data);
      } else if (reportType === "views") {
        const res = await axios.get(`${API}/admin/reports/views`, { headers });
        setViewsData(res.data);
        setData(res.data.items || []);
      } else if (reportType === "buyer_clients") {
        const res = await axios.get(`${API}/admin/reports/data?report_type=sold&date_from=${dateFrom}&date_to=${dateTo}`, { headers });
        setData(res.data);
      } else {
        const res = await axios.get(`${API}/admin/reports/data?report_type=${reportType}&date_from=${dateFrom}&date_to=${dateTo}`, { headers });
        setData(res.data);
      }
      toast.success(`${data.length || "0"} inregistrari gasite`);
    } catch (err) { toast.error("Eroare incarcare date"); }
    setLoading(false);
  };

  const getColumns = () => {
    switch (reportType) {
      case "sold": return [
        { key: "_idx", label: "Nr" }, { key: "dossier", label: "Nr Dossier" }, { key: "brand", label: "Marca" }, { key: "model", label: "Model" },
        { key: "year", label: "An" }, { key: "vin", label: "VIN" }, { key: "selling_price", label: "Pret" },
        { key: "client_name", label: "Client" }, { key: "client_phone", label: "Telefon" }, { key: "client_address", label: "Adresa" },
        { key: "sale_date", label: "Data vanzare" }, { key: "observation", label: "Observatii" },
      ];
      case "rar": return [
        { key: "_idx", label: "Nr" }, { key: "dossier", label: "Nr Dossier" }, { key: "brand", label: "Marca" }, { key: "model", label: "Model" },
        { key: "vin", label: "VIN" }, { key: "rar_date", label: "Data RAR" }, { key: "rar_status", label: "Status RAR" },
        { key: "client_name", label: "Client" }, { key: "client_phone", label: "Telefon" }, { key: "observation", label: "Observatii" },
      ];
      case "delivered": return [
        { key: "_idx", label: "Nr" }, { key: "dossier", label: "Nr Dossier" }, { key: "brand", label: "Marca" }, { key: "model", label: "Model" },
        { key: "client_name", label: "Client" }, { key: "client_phone", label: "Telefon" }, { key: "client_address", label: "Adresa livrare" },
        { key: "delivery_date", label: "Data livrare" }, { key: "observation", label: "Observatii" },
      ];
      case "registered": return [
        { key: "_idx", label: "Nr" }, { key: "dossier", label: "Nr Dossier" }, { key: "scooter_id", label: "ID" }, { key: "brand", label: "Marca" },
        { key: "model", label: "Model" }, { key: "year", label: "An" }, { key: "vin", label: "VIN" },
        { key: "status", label: "Status" }, { key: "parking", label: "Parking" }, { key: "created_at", label: "Data" }, { key: "observation", label: "Observatii" },
      ];
      case "repair": return [
        { key: "_idx", label: "Nr" }, { key: "dossier", label: "Nr Dossier" }, { key: "brand", label: "Marca" }, { key: "model", label: "Model" },
        { key: "year", label: "An" }, { key: "status", label: "Status" }, { key: "parking", label: "Parking" }, { key: "observation", label: "Observatii" },
      ];
      case "auction_clients": return [
        { key: "_idx", label: "Nr" }, { key: "name", label: "Nume" }, { key: "phone", label: "Telefon" },
        { key: "email", label: "Email" }, { key: "city", label: "Oras" }, { key: "status", label: "Status" },
        { key: "client_type", label: "Tip" }, { key: "created_at", label: "Data" }, { key: "observation", label: "Observatii" },
      ];
      case "buyer_clients": return [
        { key: "_idx", label: "Nr" }, { key: "client_name", label: "Client" }, { key: "client_phone", label: "Telefon" },
        { key: "brand", label: "Marca" }, { key: "model", label: "Model" }, { key: "selling_price", label: "Pret" },
        { key: "sale_date", label: "Data cumparare" }, { key: "observation", label: "Observatii" },
      ];
      case "custom_selection": return [
        { key: "_idx", label: "Nr" }, { key: "dossier", label: "Nr Dossier" }, { key: "brand", label: "Marca" }, { key: "model", label: "Model" },
        { key: "vin", label: "Nr Matriculare/VIN" }, { key: "year", label: "An" }, { key: "selling_price", label: "Pret cerut" },
        { key: "pret_offert", label: "Pret oferit" }, { key: "observation", label: "Observatii" },
        { key: "comentariu", label: "Comentariu" }, { key: "da", label: "DA" }, { key: "nu", label: "NU" },
      ];
      case "views": return [
        { key: "_idx", label: "Nr" }, { key: "type", label: "Tip" }, { key: "id", label: "ID" },
        { key: "title", label: "Produs" }, { key: "views", label: "Vizualizari" }, { key: "status", label: "Status" },
      ];
      default: return [];
    }
  };

  const handleDownloadPDF = () => {
    if (data.length === 0) { toast.error("Nu sunt date"); return; }
    const title = REPORT_TYPES.find(r => r.id === reportType)?.label || "Raport";
    const doc = generateReportPDF(data, title, getColumns(), dateFrom, dateTo);
    doc.save(`raport_${reportType}_${dateFrom || "tot"}_${dateTo || "tot"}.pdf`);
    toast.success("PDF generat!");
  };

  const handleDownloadCSV = () => {
    if (data.length === 0) { toast.error("Nu sunt date"); return; }
    const csv = generateCSV(data, getColumns());
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `raport_${reportType}_${dateFrom || "tot"}_${dateTo || "tot"}.csv`;
    a.click();
    toast.success("CSV/Excel generat!");
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="reports-page">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText className="w-7 h-7 text-primary" /> Rapoarte si extrase
        </h1>

        {/* Report type selection */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {REPORT_TYPES.map(rt => (
            <button key={rt.id} onClick={() => { setReportType(rt.id); setData([]); }}
              className={`p-3 rounded-lg border text-left text-sm transition-all ${reportType === rt.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/30"}`}>
              <rt.icon className={`w-5 h-5 ${rt.color} mb-1`} />
              <p className="font-semibold text-xs">{rt.label}</p>
            </button>
          ))}
        </div>

        {/* Date range + Generate */}
        {reportType && (
          <Card>
            <CardContent className="p-4 flex flex-wrap gap-3 items-end">
              {reportType !== "views" && (
                <>
                  <div className="space-y-1">
                    <label className="text-xs font-medium">De la:</label>
                    <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-40" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium">Pana la:</label>
                    <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-40" />
                  </div>
                </>
              )}
              <Button onClick={fetchReport} disabled={loading}>
                {loading ? "Se incarca..." : "Genereaza"}
              </Button>
              {data.length > 0 && (
                <>
                  <Button variant="outline" onClick={handleDownloadPDF}><Download className="w-4 h-4 mr-1" /> PDF</Button>
                  <Button variant="outline" onClick={handleDownloadCSV}><Download className="w-4 h-4 mr-1" /> CSV/Excel</Button>
                </>
              )}
              {data.length > 0 && <Badge>{data.length} inregistrari</Badge>}
            </CardContent>
          </Card>
        )}

        {/* Views report */}
        {reportType === "views" && viewsData && (
          <Card>
            <CardContent className="p-4">
              <p className="text-lg font-bold mb-3">Total vizualizari: {viewsData.total_views}</p>
            </CardContent>
          </Card>
        )}

        {/* Data table */}
        {data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-secondary">
                  {getColumns().map(col => (
                    <th key={col.key} className="text-left py-2 px-2 border border-border/30 font-semibold">{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.slice(0, 100).map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? "bg-card" : "bg-secondary/20"}>
                    {getColumns().map(col => (
                      <td key={col.key} className="py-1.5 px-2 border border-border/20">
                        {col.key === "_idx" ? idx + 1 : String(row[col.key] || "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {data.length > 100 && <p className="text-xs text-muted-foreground text-center py-2">Se afiseaza primele 100 din {data.length}</p>}
          </div>
        )}
      </div>
    </Layout>
  );
}
